/**
 * Kalma Wellness — Main JS
 * Modules: Cart, MobileMenu, Search, Header, UI
 */

'use strict';

// ─── UTILS ───────────────────────────────────────────
const $ = id => document.getElementById(id);
const $$ = sel => document.querySelectorAll(sel);

function on(el, event, fn) {
  if (el) el.addEventListener(event, fn);
}

function debounce(fn, ms = 300) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

// ─── CART MODULE ─────────────────────────────────────
const Cart = (() => {
  const drawer  = $('cartDrawer');
  const body    = $('cartDrawerBody');
  const countEl = $('cartCount');
  const drawerCountEl = $('cartDrawerCount');
  const subtotalEl = $('cartSubtotal');
  const freeShipEl = $('cartFreeShip');
  const btn     = $('cartToggle');
  const closeBtn = $('cartDrawerClose');
  const overlay = $('drawerOverlay');

  async function refresh() {
    try {
      const res  = await fetch('/cart.js');
      const data = await res.json();
      if (countEl) countEl.textContent = data.item_count;
      if (drawerCountEl) drawerCountEl.textContent = `(${data.item_count})`;
      if (subtotalEl) subtotalEl.textContent = formatMoney(data.total_price);
      if (freeShipEl) {
        freeShipEl.innerHTML = data.total_price < 5000
          ? `Add <strong>${formatMoney(5000 - data.total_price)}</strong> more for free shipping`
          : '✦ You qualify for free shipping!';
      }
      if (body) body.innerHTML = renderItems(data);
    } catch(e) {
      console.error('[Cart] refresh failed:', e);
    }
  }

  function renderItems(data) {
    if (!data.item_count) {
      return '<div class="cart-empty"><div class="cart-empty-icon">🛍</div><p>Your bag is empty</p><a href="/collections/all" class="btn-gold">Start Shopping</a></div>';
    }
    return data.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item-img"><img src="${item.image}" alt="${item.title}" loading="lazy"></div>
        <div class="cart-item-info">
          <p class="cart-item-title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item-variant">${item.variant_title}</p>` : ''}
          <div class="cart-item-actions">
            <button class="cart-qty-btn" data-key="${item.key}" data-qty="${item.quantity - 1}" aria-label="Decrease quantity">−</button>
            <span class="cart-item-qty">${item.quantity}</span>
            <button class="cart-qty-btn" data-key="${item.key}" data-qty="${item.quantity + 1}" aria-label="Increase quantity">+</button>
            <span class="cart-item-price">${formatMoney(item.line_price)}</span>
          </div>
        </div>
      </div>`).join('');
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  async function updateQty(key, qty) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      await refresh();
    } catch(e) {
      console.error('[Cart] updateQty failed:', e);
    }
  }

  function open() {
    refresh();
    drawer?.classList.add('open');
    drawer?.setAttribute('aria-hidden', 'false');
    overlay?.classList.add('visible');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    drawer?.classList.remove('open');
    drawer?.setAttribute('aria-hidden', 'true');
    if (!$('mobileDrawer')?.classList.contains('open')) {
      overlay?.classList.remove('visible');
      document.body.style.overflow = '';
    }
  }

  function init() {
    on(btn, 'click', e => { e.preventDefault(); open(); });
    on(closeBtn, 'click', close);
    on(overlay, 'click', close);

    // Qty buttons (delegated)
    body?.addEventListener('click', e => {
      const qtyBtn = e.target.closest('.cart-qty-btn');
      if (!qtyBtn) return;
      const { key, qty } = qtyBtn.dataset;
      updateQty(key, parseInt(qty));
    });

    // Close drawer when navigating away via its own links (View Bag, Checkout)
    drawer?.addEventListener('click', e => {
      if (e.target.closest('a')) close();
    });
  }

  return { init, open, close, refresh, updateQty };
})();

// ─── MOBILE MENU MODULE ───────────────────────────────
const MobileMenu = (() => {
  const drawer  = $('mobileDrawer');
  const openBtn = $('mobileMenuBtn');
  const closeBtn = $('mobileDrawerClose');
  const overlay = $('drawerOverlay');

  function open() {
    drawer?.classList.add('open');
    overlay?.classList.add('visible');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    drawer?.classList.remove('open');
    if (!$('cartDrawer')?.classList.contains('open')) {
      overlay?.classList.remove('visible');
      document.body.style.overflow = '';
    }
  }

  function init() {
    on(openBtn, 'click', open);
    on(closeBtn, 'click', close);
    on(overlay, 'click', close);
    // Close on nav link click
    drawer?.querySelectorAll('a').forEach(a => on(a, 'click', close));
  }

  return { init, open, close };
})();

// ─── SEARCH MODULE ────────────────────────────────────
const Search = (() => {
  const panel  = $('searchPanel');
  const input  = $('searchInput');
  const openBtn = $('searchToggle');
  const closeBtn = $('searchClose');
  const results = $('searchResults');

  async function suggest(q) {
    if (!q || q.length < 2) { if(results) results.innerHTML = ''; return; }
    try {
      const res  = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`);
      const data = await res.json();
      const products = data.resources?.results?.products || [];
      if (!results) return;
      results.innerHTML = products.length
        ? products.map(p => `<a href="${p.url}" class="search-result-item"><img src="${p.image}" alt="${p.title}" loading="lazy"><span>${p.title}</span></a>`).join('')
        : '<p class="search-no-results">No results found</p>';
    } catch(e) {
      console.error('[Search] suggest failed:', e);
    }
  }

  function open() {
    panel?.classList.add('open');
    setTimeout(() => input?.focus(), 100);
  }

  function close() {
    panel?.classList.remove('open');
    if (input) input.value = '';
    if (results) results.innerHTML = '';
  }

  function init() {
    on(openBtn, 'click', open);
    on(closeBtn, 'click', close);
    on(input, 'input', debounce(e => suggest(e.target.value)));
    on(input, 'keydown', e => {
      if (e.key === 'Escape') close();
      if (e.key === 'Enter') window.location = `/search?q=${encodeURIComponent(input.value)}`;
    });
  }

  return { init };
})();

// ─── HEADER MODULE ────────────────────────────────────
const Header = (() => {
  const header = document.querySelector('.site-header');
  const logo   = header?.querySelector('.logo-img');

  function onScroll() {
    const scrolled = window.scrollY > 80;
    header?.classList.toggle('scrolled', scrolled);
    if (logo) {
      const isMobile = window.innerWidth <= 991;
      logo.style.height = scrolled
        ? (isMobile ? '28px' : '44px')
        : (isMobile ? '36px' : '56px');
    }
  }

  function init() {
    window.addEventListener('scroll', debounce(onScroll, 10), { passive: true });
  }

  return { init };
})();

// ─── ANNOUNCEMENT BAR ─────────────────────────────────
const AnnouncementBar = (() => {
  function init() {
    const bar   = $('announcementBar');
    const close = $('announcementClose');
    if (!bar || !close) return;

    // Don't show if dismissed this session
    if (sessionStorage.getItem('announcement-dismissed')) {
      bar.style.display = 'none';
      return;
    }
    on(close, 'click', () => {
      bar.style.display = 'none';
      sessionStorage.setItem('announcement-dismissed', '1');
    });
  }

  return { init };
})();

// ─── CART ADD (product pages) ─────────────────────────
async function addToCart(variantId, qty = 1) {
  const btn = document.querySelector('.btn-add-to-cart');
  if (btn) { btn.textContent = 'Adding...'; btn.disabled = true; }

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    });
    if (!res.ok) throw new Error('Add to cart failed');
    await Cart.refresh();
    Cart.open();
  } catch(e) {
    console.error('[Cart] addToCart failed:', e);
    alert('Sorry, something went wrong. Please try again.');
  } finally {
    if (btn) { btn.textContent = 'Add to Cart'; btn.disabled = false; }
  }
}

// ─── MARQUEE PAUSE ON HOVER ───────────────────────────
function initMarquee() {
  const marquee = document.querySelector('.marquee-track');
  if (!marquee) return;
  on(marquee, 'mouseenter', () => marquee.style.animationPlayState = 'paused');
  on(marquee, 'mouseleave', () => marquee.style.animationPlayState = 'running');
}

// ─── LAZY IMAGES ──────────────────────────────────────
function initLazyImages() {
  if (!('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
        observer.unobserve(img);
      }
    });
  }, { rootMargin: '200px' });

  $$('img[data-src]').forEach(img => observer.observe(img));
}



// ─── BACK TO TOP ──────────────────────────────────────
function initBackToTop() {
  const btn = $('backToTop');
  if (!btn) return;

  window.addEventListener('scroll', debounce(() => {
    btn.classList.toggle('visible', window.scrollY > 400);
  }, 50), { passive: true });

  on(btn, 'click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

// ─── COOKIE CONSENT ───────────────────────────────────
const CookieConsent = (() => {
  function init() {
    // Don't show if already answered
    if (localStorage.getItem('kalma-cookies')) return;

    const banner = $('cookieBanner');
    if (!banner) return;

    // Show after 1.5s
    setTimeout(() => banner.classList.add('visible'), 1500);

    on($('cookieAccept'), 'click', () => {
      localStorage.setItem('kalma-cookies', 'accepted');
      banner.classList.remove('visible');
      // Enable analytics if needed
    });

    on($('cookieDecline'), 'click', () => {
      localStorage.setItem('kalma-cookies', 'declined');
      banner.classList.remove('visible');
    });
  }

  return { init };
})();

// ─── SCROLL FADE ANIMATIONS ──────────────────────────
function initScrollAnimations() {
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.fade-in').forEach(el => observer.observe(el));
}

// ─── INIT ─────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  Cart.init();
  MobileMenu.init();
  Search.init();
  Header.init();
  AnnouncementBar.init();
  initMarquee();
  initLazyImages();
  initScrollAnimations();
  initBackToTop();
  CookieConsent.init();
});
